import { useEffect, useState } from 'react';
import { Input } from './Input';
import { Select } from './Select';

/** The option value that reveals the text input. Never a real make or model. */
export const OTHER_OPTION = '__other__';

export interface CatalogSelectProps {
  id: string;
  value: string;
  options: readonly string[];
  onChange: (value: string) => void;
  placeholder: string;
  otherLabel: string;
  otherPlaceholder?: string;
  hasError?: boolean;
  disabled?: boolean;
  'aria-required'?: boolean;
}

const sameText = (left: string, right: string): boolean => left.trim().toLowerCase() === right.trim().toLowerCase();

/**
 * A drop-down over the values the workspace already uses, with **"Other…"** for the one it does not
 * know yet (business review 2026-09-23, plan §11.19). There is no make/model catalogue table: the options
 * are what the templates already say, so a new make is typed once — on its template — and offered from
 * then on. A value that is not among the options (a legacy free-text entry, or one typed under "Other…")
 * opens in the text input, so nothing already recorded is ever hidden or lost.
 */
export const CatalogSelect = ({
  id,
  value,
  options,
  onChange,
  placeholder,
  otherLabel,
  otherPlaceholder,
  hasError = false,
  disabled = false,
  'aria-required': ariaRequired,
}: CatalogSelectProps) => {
  const known = options.find((option) => sameText(option, value));
  const [isOther, setIsOther] = useState(value.trim() !== '' && known === undefined);

  // A value that arrives from outside (a template applied, the server's answer) decides the mode again.
  useEffect(() => {
    if (value.trim() !== '' && options.every((option) => !sameText(option, value))) {
      setIsOther(true);
    }
  }, [options, value]);

  return (
    <div className="space-y-2">
      <Select
        id={id}
        value={isOther ? OTHER_OPTION : (known ?? '')}
        placeholder={placeholder}
        hasError={hasError && !isOther}
        disabled={disabled}
        aria-required={ariaRequired}
        onChange={(event) => {
          if (event.target.value === OTHER_OPTION) {
            setIsOther(true);
            onChange(known === undefined ? value : '');

            return;
          }

          setIsOther(false);
          onChange(event.target.value);
        }}
        options={[...options.map((option) => ({ value: option, label: option })), { value: OTHER_OPTION, label: otherLabel }]}
      />
      {isOther ? (
        <Input
          id={`${id}-other`}
          aria-label={otherLabel}
          value={value}
          placeholder={otherPlaceholder}
          hasError={hasError}
          disabled={disabled}
          aria-required={ariaRequired}
          onChange={(event) => onChange(event.target.value)}
        />
      ) : null}
    </div>
  );
};
